import { useEffect, useState, type ReactNode } from 'react'
import { AccountEnvContext, isApiError } from './client'
import type { AccountClient, AccountEnv } from './client'
import type { CustomerProfile } from './types'
import {
  ProfileHeader, ActiveSessionCard, PlatesSection, PaymentMethodSection,
  SupportSection, SettingsSection, PrivacyScreen, CommPrefsScreen,
} from './tabs/AccountTab'
import { HistoryList } from './tabs/HistoryTab'
import { PermitsSection } from './tabs/PermitsTab'
import { BackButton } from './primitives'
import { AveryChat } from './AveryChat'

/**
 * Account screen (account.parkify.com) — re-skinned to the boss's UX redesign
 * "slice 2" (the demo's AccountSidebar), rendered as the full-page portal
 * rather than a phone slide-over. Presentation-only over the existing portal
 * API: profile, plates, live session, session history + receipts, saved-card
 * management, communication preferences, support/legal links, sign-out.
 *
 * Demo features that started life fabricated now render from real endpoints,
 * added as slice-2 follow-ups: the per-row fee split (#904), temporary plates
 * (#906), and the Permits tab (this slice — GET /api/portal/subscriptions +
 * /permits). We only ever render what the API returns — the client-side
 * total/1.198 fee math was NOT ported; the itemized breakdown lives in the
 * downloadable receipt PDF.
 */

export function AccountExperience(props: {
  client: AccountClient
  billingPortalEnabled: boolean
  onFindParking: () => void
  onSignInAgain: () => void
  onSignOut: () => void
  focusSessionId?: string | null
  onSessionRefreshed?: (token: string, csrf?: string) => void
  /** Host-provided add-a-card form. See AccountEnv.renderAddCard — the Stripe
   *  dependency stays in the host so this package never handles card details. */
  renderAddCard?: AccountEnv['renderAddCard']
}) {
  const { client, billingPortalEnabled, onFindParking, onSignInAgain, onSignOut, focusSessionId, onSessionRefreshed, renderAddCard } = props
  const env: AccountEnv = { client, billingPortalEnabled, onFindParking, onSignInAgain, focusSessionId, onSessionRefreshed, renderAddCard }

  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  // `isAuth` separates a genuine "your session ended" (401 — the api layer has
  // already cleared the token, so re-auth is the only way forward) from a
  // transient load failure (500 / timeout / network blip) where the token is
  // still valid and a retry is all that's needed.
  const [error, setError] = useState<{ msg: string; isAuth: boolean } | null>(null)
  const [reloadCount, setReloadCount] = useState(0)
  const [tab, setTab] = useState<'account' | 'history' | 'permits'>('account')
  // Settings sub-screens (Privacy, Communication preferences) take over the
  // whole panel with a back button — local view state, no routing (matches the
  // Permit detail pattern). Cleared back to the tabbed account view on Back.
  const [subScreen, setSubScreen] = useState<'privacy' | 'comms' | 'avery' | null>(null)
  // Which History row should arrive expanded + scrolled into view. Seeded from
  // the deep-link prop, but not frozen to it — the active-session card's
  // "Details" / "+ Add time" buttons update this too, so they land the parker
  // on THEIR session instead of just the generic (unfocused) History tab.
  const [focusSession, setFocusSession] = useState<string | null>(focusSessionId ?? null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    void (async () => {
      try {
        const me = await client.fetchMe()
        if (!cancelled) setProfile(me)
      } catch (err) {
        if (cancelled) return
        const isAuth = isApiError(err) && err.status === 401
        const msg = isApiError(err) ? err.message : 'Could not load your account.'
        setError({ msg, isAuth })
      }
    })()
    return () => { cancelled = true }
  }, [reloadCount])

  // A deep link from the reminder SMS (`/?session={txnId}`) lands the parker on
  // their session — open the History tab so the focused row is visible. The host
  // parses the id from the URL and passes it in as focusSessionId (frozen for the
  // mount), so this effectively fires once on mount. Hooks stay above the
  // branching below to keep call order stable across renders.
  useEffect(() => {
    if (focusSessionId) { setTab('history'); setFocusSession(focusSessionId) }
  }, [focusSessionId])

  let body: ReactNode
  if (error) {
    body = (
      <div className="pka-card">
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Something went wrong</h1>
        <p style={{ margin: '0 0 16px', color: 'var(--color-gray-700)' }}>{error.msg}</p>
        {error.isAuth ? (
          <button type="button" className="pka-btn-text" onClick={onSignInAgain}>Sign in again →</button>
        ) : (
          <button type="button" className="pka-btn-primary" onClick={() => setReloadCount(n => n + 1)}
            style={{ fontSize: 13, padding: '10px 18px' }}>
            Retry
          </button>
        )}
      </div>
    )
  } else if (!profile) {
    body = (
      <div className="pka-card" style={{ textAlign: 'center', padding: '48px 28px' }}>
        <div style={{
          width: 32, height: 32,
          border: '3px solid var(--color-brand-light)',
          borderTopColor: 'var(--color-brand)',
          borderRadius: '50%', margin: '0 auto',
          animation: 'pka-spin 0.8s linear infinite',
        }} />
      </div>
    )
  } else {
    const onProfilePatched = (updated: Omit<CustomerProfile, 'visits'>) => {
      setProfile(p => (p ? { ...updated, visits: p.visits } : p))
    }

    // A live session now correctly carries ended_at=null (the cs read-time
    // liveness derivation). Exclude a refunded row so a refund can't masquerade
    // as the active session.
    const activeVisit = profile.visits.find(v => !v.ended_at && v.status !== 'refunded') ?? null

    if (subScreen === 'privacy') {
      body = <PrivacyScreen onBack={() => setSubScreen(null)} />
    } else if (subScreen === 'comms') {
      body = <CommPrefsScreen profile={profile} onSaved={onProfilePatched} onBack={() => setSubScreen(null)} />
    } else if (subScreen === 'avery') {
      body = <AveryChat onClose={() => setSubScreen(null)} />
    } else {
      body = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* The account page is a dead end without this: it's a full-page route
              (not a slide-over the parker can just swipe away), and nothing else
              here leads back to the browsable "find parking" home. Not shown on
              the find surface itself — only here, on the account page. */}
          <BackButton onBack={onFindParking} />
          <ProfileHeader profile={profile} onSaved={onProfilePatched} />

          <div className="pka-tabs" role="tablist" aria-label="Account sections">
            {([['account', 'Account'], ['history', 'History'], ['permits', 'Permits']] as const).map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                className={`pka-tab${tab === id ? ' pka-tab--active' : ''}`}
                onClick={() => setTab(id)}
              >{label}</button>
            ))}
          </div>

          {tab === 'account' && (
            <div className="pka-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 16 }}>
              <div className="pka-card">
                <ActiveSessionCard
                  visit={activeVisit}
                  onManage={(txnId) => { setTab('history'); setFocusSession(txnId) }}
                />
                <PlatesSection
                  plates={profile.plates}
                  onPlatesChange={next => setProfile(p => (p ? { ...p, plates: next } : p))}
                />
                <PaymentMethodSection />
                <SupportSection onOpenAvery={() => setSubScreen('avery')} />
                <SettingsSection
                  onOpenPrivacy={() => setSubScreen('privacy')}
                  onOpenComms={() => setSubScreen('comms')}
                  onSignOut={onSignOut}
                />
              </div>
            </div>
          )}

          {tab === 'permits' && (
            <div className="pka-fadein" style={{ marginTop: 16 }}>
              <div className="pka-card"><PermitsSection /></div>
            </div>
          )}

          {tab === 'history' && (
            <div className="pka-fadein" style={{ marginTop: 16 }}>
              <HistoryList
                visits={profile.visits}
                hasPlates={profile.plates.length > 0}
                focusSession={focusSession}
                email={profile.email || client.getEmail()}
              />
            </div>
          )}
        </div>
      )
    }
  }

  return (
    <AccountEnvContext.Provider value={env}>
      {/* pka--chat stretches to fill the host panel's remaining height (via
          flex, when the host is itself a flex column — see account.css) so
          Avery's message list can scroll internally while its composer stays
          pinned to the bottom, instead of the whole panel scrolling as one
          block. Every other screen keeps the plain content-sized `.pka`. */}
      <div className={subScreen === 'avery' ? 'pka pka--chat' : 'pka'}>{body}</div>
    </AccountEnvContext.Provider>
  )
}
